import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import SpeedIcon from '@mui/icons-material/Speed';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';

import { api } from '../../services/api';
import { resolveIperfTestChannelBand } from '../../utils/iperfChannelUtils';
import { DEFAULT_IPERF_SETTINGS, buildIperfCapPayload, describeOfferedRate } from '../../utils/iperfScheduleUtils';
import {
  IPERF_COMPARE_DIFF_PCT, IPERF_COMPARE_LOSS_PP, compareIperfSuites, describeCompareSummary, filterCompareRows,
  iperfCompareTypeLabel,
} from '../../utils/iperfCompare';
import IperfTestSettingsFields, { getIperfCoverageLabels } from './IperfTestSettingsFields';
import { groupIperfTests, unwrapIperfRetryResponse } from '../../utils/iperfTestGroups';

const STATUS_META = {
  queued:    { label: 'Queued',    color: 'info'    },
  running:   { label: 'Running',   color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
  failed:    { label: 'Failed',    color: 'error'   },
  cancelled: { label: 'Cancelled', color: 'default' },
  pending:   { label: 'Pending',   color: 'default' },
  timed_out: { label: 'Timed Out', color: 'error'   },
};

const isTerminal = (status) => ['completed', 'failed', 'cancelled'].includes(status);

const fmt = (v) => (typeof v === 'number' ? v.toFixed(1) : '—');
const fmtSigned = (v) => (typeof v === 'number' ? `${v > 0 ? '+' : ''}${v.toFixed(1)}` : '—');

const findRawPerf = (raw) => raw?.perf ?? raw?.status?.perf ?? null;

const resolveThroughput = (t) => {
  if (typeof t?.throughput_mbps === 'number' && t.throughput_mbps >= 0) {
    return t.throughput_mbps;
  }
  const perf = findRawPerf(t?.raw_result);
  if (typeof perf?.bw !== 'number') return null;
  const unit = String(perf.bw_unit || '').toLowerCase();
  if (unit.includes('gbits')) return perf.bw * 1000;
  if (unit.includes('kbits')) return perf.bw / 1000;
  return perf.bw;
};

const resolveLoss = (t) => {
  if (typeof t.packet_loss_pct === 'number' && t.packet_loss_pct >= 0) return t.packet_loss_pct;
  const dl = findRawPerf(t.raw_result)?.data_loss;
  if (dl == null) return null;
  const parsed = parseFloat(String(dl).replace('%', ''));
  return isNaN(parsed) ? null : parsed;
};

const resolveThroughputMbps = (t) => resolveThroughput(t);

const getDominant = (countsMap) => {
  if (!countsMap || typeof countsMap !== 'object') return null;
  const entries = Object.entries(countsMap);
  if (entries.length === 0) return null;
  let maxKey = entries[0][0];
  let maxVal = entries[0][1];
  for (let i = 1; i < entries.length; i++) {
    if (entries[i][1] > maxVal) {
      maxVal = entries[i][1];
      maxKey = entries[i][0];
    }
  }
  return maxKey;
};

const resolveRfStats = (t) => {
  const perf = findRawPerf(t?.raw_result);
  if (!perf) return { quality: null, bw: null, mcs: null, nss: null };
  const stats = perf.stats;
  return {
    quality: typeof perf.Quality === 'number' ? perf.Quality : (typeof perf.quality === 'number' ? perf.quality : null),
    bw: getDominant(stats?.bw_counts),
    mcs: getDominant(stats?.mcs_counts),
    nss: getDominant(stats?.nss_counts),
  };
};

const truncMcs = (mcs) => {
  if (!mcs) return '—';
  const match = mcs.match(/^(HE )?MCS (\d+)/);
  return match ? `MCS ${match[2]}` : mcs;
};

const computeDisplayedPathAnalysis = (path, tests) => {
  const e2e = tests.find(t =>
    t.type === 'end_to_end' &&
    t.from_serial?.toLowerCase() === path.leaf_serial?.toLowerCase() &&
    t.to_serial?.toLowerCase() === path.cap_serial?.toLowerCase()
  );
  const e2eMbps = resolveThroughputMbps(e2e) ?? path.e2e_throughput_mbps;

  const hopByChild = new Map();
  tests.filter(t => t.type === 'hop').forEach(t => {
    if (t.from_serial) hopByChild.set(t.from_serial.toLowerCase(), t);
  });

  const hopThroughputs = [];
  const hopChain = [];
  let cur = path.leaf_serial?.toLowerCase();
  const cap = path.cap_serial?.toLowerCase();
  const visited = new Set();
  while (cur && cur !== cap && !visited.has(cur)) {
    visited.add(cur);
    const hop = hopByChild.get(cur);
    if (!hop) break;
    hopChain.push(hop);
    const mbps = resolveThroughputMbps(hop);
    if (mbps != null) hopThroughputs.push(mbps);
    cur = hop.to_serial?.toLowerCase();
  }

  const bottleneck = hopThroughputs.length
    ? Math.min(...hopThroughputs)
    : (path.bottleneck_hop_mbps || null);
  const hops = path.hops || e2e?.path_hops || hopThroughputs.length;
  const expected = bottleneck != null
    ? bottleneck * Math.pow(0.95, hops)
    : path.expected_e2e_mbps;
  const rawDegradation = expected > 0 && e2eMbps != null
    ? ((expected - e2eMbps) / expected) * 100
    : path.degradation_pct;
  // Clamp to 0 — negative means E2E exceeded expected, which is healthy
  const degradation = typeof rawDegradation === 'number'
    ? Math.max(0, rawDegradation)
    : rawDegradation;

  return {
    ...path,
    e2e_throughput_mbps: e2eMbps,
    bottleneck_hop_mbps: bottleneck,
    expected_e2e_mbps: expected,
    degradation_pct: degradation,
    healthy: typeof degradation === 'number' ? degradation <= 10 : null,
    hopChain,
    e2eTest: e2e,
  };
};

// Build merged path data from an E2E test, overlaying analysis when available.
const buildPathForE2ETest = (e2eTest, analysisPaths, tests) => {
  const matchingPath = analysisPaths?.find(p =>
    p.leaf_serial?.toLowerCase() === e2eTest.from_serial?.toLowerCase() &&
    p.cap_serial?.toLowerCase() === e2eTest.to_serial?.toLowerCase()
  );
  const basePath = matchingPath || {
    leaf_serial: e2eTest.from_serial,
    cap_serial: e2eTest.to_serial,
    hops: e2eTest.path_hops,
    e2e_throughput_mbps: null,
    bottleneck_hop_mbps: null,
    expected_e2e_mbps: null,
    degradation_pct: null,
    healthy: null,
    flagged_hop: null,
  };
  return computeDisplayedPathAnalysis(basePath, tests);
};

const relativeTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const RF_STATS_TOOLTIP = [
  'Q  — Link quality score (0–100)',
  'BW — Dominant channel width used during the test (e.g. 80MHZ)',
  'MCS — Most-used modulation & coding scheme (higher = faster)',
  'NSS — Spatial streams used (nss-4 = 4×4 MIMO)',
].join('\n');

const RfStatsCell = ({ test }) => {
  const rf = resolveRfStats(test);
  if (rf.quality == null && !rf.bw && !rf.mcs && !rf.nss) return <TableCell align="right">—</TableCell>;
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{RF_STATS_TOOLTIP}</span>} placement="left">
      <TableCell align="right" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap', cursor: 'help' }}>
        {rf.quality != null && <span>Q:{rf.quality} </span>}
        {rf.bw && <span>BW:{rf.bw} </span>}
        {rf.mcs && <span>{truncMcs(rf.mcs)} </span>}
        {rf.nss && <span>{rf.nss}</span>}
      </TableCell>
    </Tooltip>
  );
};

// Build a lookup of serial → device from the devices prop.
const buildDeviceLookup = (devices) => {
  const m = new Map();
  (devices || []).forEach((d) => {
    if (d?.serial_number) m.set(d.serial_number.toLowerCase(), d);
  });
  return m;
};

// Return the uplink channel/band label for the child device (from_serial).
// ul_band: "5GHz" | "2.4GHz" | "6GHz"; ul_channel: integer channel number.
const resolveChannelBand = (test, deviceLookup, reverseDirection = false) =>
  resolveIperfTestChannelBand(test, deviceLookup, { reverseDirection });

const capSerialForRun = (run) => {
  if (!run) return null;
  if (Array.isArray(run.cap_serials) && run.cap_serials.length === 1) {
    return run.cap_serials[0];
  }
  if (run.cap_serial && !String(run.cap_serial).includes('CAPs')) {
    return run.cap_serial;
  }
  return null;
};

const runCapLabel = (run, deviceLookup) => {
  if (!run) return '—';
  if (run.cap_serials?.length > 1) {
    return `${run.cap_serials.length} CAPs`;
  }
  const serial = capSerialForRun(run);
  if (!serial) return '—';
  const dev = deviceLookup?.get?.(serial.toLowerCase());
  return dev?.name || serial;
};

const runDisplayName = (run, deviceLookup) => {
  if (!run) return '—';
  const name = run.name?.trim();
  if (name) return name;
  const cap = runCapLabel(run, deviceLookup);
  return cap !== '—' ? `${cap} iperf test` : '—';
};

const runLabel = (run, deviceLookup) => {
  if (!run) return '—';
  return `${runDisplayName(run, deviceLookup)} · ${relativeTime(run.created_at)}`;
};

const defaultIperfRunName = (selectedCapSerials, allCaps) => {
  if (selectedCapSerials.length === 0) return '';
  if (allCaps.length > 1 && selectedCapSerials.length === allCaps.length) {
    return 'Site-wide iperf test';
  }
  if (selectedCapSerials.length > 1) {
    return `${selectedCapSerials.length} CAPs iperf test`;
  }
  const serial = selectedCapSerials[0];
  const capDevice = allCaps.find((d) => d.serial_number === serial);
  const label = capDevice?.name || serial;
  return `${label} iperf test`;
};

const resolveHopRootCap = (fromSerial, toSerial, deviceLookup, capSet) => {
  const to = toSerial?.toLowerCase();
  if (to && capSet.has(to)) return to;

  let cur = fromSerial?.toLowerCase();
  const visited = new Set();
  while (cur && !visited.has(cur)) {
    visited.add(cur);
    if (capSet.has(cur)) return cur;
    const parent = deviceLookup.get(cur)?.parent_serial?.toLowerCase();
    if (!parent) break;
    cur = parent;
  }
  return null;
};

const resolveCapSections = (detailRun, deviceLookup) => {
  const tests = detailRun?.tests || [];
  const reverse = Boolean(detailRun?.reverse_direction);
  const multiCaps = Array.isArray(detailRun?.cap_serials) && detailRun.cap_serials.length > 1
    ? detailRun.cap_serials
    : null;
  const singleCap = detailRun?.cap_serial && !String(detailRun.cap_serial).includes('CAPs')
    ? detailRun.cap_serial
    : null;

  const allToCapCapSerial = (t) => (reverse ? t.from_serial : t.to_serial)?.toLowerCase();

  if (!multiCaps) {
    const capSerial = singleCap || detailRun?.cap_serial || '—';
    return [{
      capSerial,
      reverse,
      hopTests: tests.filter((t) => t.type === 'hop'),
      e2eTests: tests.filter((t) => t.type === 'end_to_end'),
      allToCapTests: tests.filter((t) => t.type === 'all_to_cap'),
      analysisPaths: detailRun?.analysis?.paths || [],
    }];
  }

  const capSet = new Set(multiCaps.map((s) => s.toLowerCase()));
  const hopByCap = new Map(multiCaps.map((c) => [c.toLowerCase(), []]));
  const e2eByCap = new Map(multiCaps.map((c) => [c.toLowerCase(), []]));
  const allToCapByCap = new Map(multiCaps.map((c) => [c.toLowerCase(), []]));

  tests.forEach((t) => {
    if (t.type === 'end_to_end') {
      const cap = (reverse ? t.from_serial : t.to_serial)?.toLowerCase();
      if (cap && e2eByCap.has(cap)) e2eByCap.get(cap).push(t);
      return;
    }
    if (t.type === 'all_to_cap') {
      const cap = allToCapCapSerial(t);
      if (cap && allToCapByCap.has(cap)) allToCapByCap.get(cap).push(t);
      return;
    }
    if (t.type === 'hop') {
      const cap = resolveHopRootCap(t.from_serial, t.to_serial, deviceLookup, capSet);
      if (cap && hopByCap.has(cap)) hopByCap.get(cap).push(t);
    }
  });

  return multiCaps.map((cap) => {
    const key = cap.toLowerCase();
    return {
      capSerial: cap,
      reverse,
      hopTests: hopByCap.get(key) || [],
      e2eTests: e2eByCap.get(key) || [],
      allToCapTests: allToCapByCap.get(key) || [],
      analysisPaths: (detailRun?.analysis?.paths || []).filter(
        (p) => p.cap_serial?.toLowerCase() === key,
      ),
    };
  });
};

const pathExpandKey = (capSerial, index) => `${String(capSerial).toLowerCase()}:${index}`;

const EMPTY_DEVICES = [];

const IperfTestPanel = ({
  devices = [],
  isActive = true,
  initialCapSerials = null,
  initialFocusSerials = null,
  onCapScopeApplied,
  initialRunId = null,
  onInitialRunApplied,
}) => {
  const safeDevices = devices ?? EMPTY_DEVICES;
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startLoading, setStartLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [singleRerunTestId, setSingleRerunTestId] = useState(null);
  const [error, setError] = useState(null);
  const [queuedInfo, setQueuedInfo] = useState(null);

  const [selectedCapSerials, setSelectedCapSerials] = useState([]);
  const [mapFocusSerials, setMapFocusSerials] = useState([]);
  const [testName, setTestName] = useState('');
  const [duration, setDuration] = useState(DEFAULT_IPERF_SETTINGS.duration);
  const [protocol, setProtocol] = useState(DEFAULT_IPERF_SETTINGS.protocol);
  const [udpBandwidth, setUdpBandwidth] = useState(DEFAULT_IPERF_SETTINGS.udpBandwidth);
  const [udpBandwidthAuto, setUdpBandwidthAuto] = useState(DEFAULT_IPERF_SETTINGS.udpBandwidthAuto);
  const [parallelTrees, setParallelTrees] = useState(DEFAULT_IPERF_SETTINGS.parallelTrees);
  const [includeHopTests, setIncludeHopTests] = useState(DEFAULT_IPERF_SETTINGS.includeHop);
  const [includeE2ETests, setIncludeE2ETests] = useState(DEFAULT_IPERF_SETTINGS.includeE2E);
  const [includeAllToCapTests, setIncludeAllToCapTests] = useState(DEFAULT_IPERF_SETTINGS.includeAllToCap);
  const [reverseDirection, setReverseDirection] = useState(false);
  const [showParams, setShowParams] = useState(false);

  const [selectedRun, setSelectedRun] = useState(null);
  // Runs opened in their own tab ('tests' is the list), and which tests
  // the user expanded or collapsed (default: only the newest is open).
  const [runTabs, setRunTabs] = useState([]);
  const [activeTab, setActiveTab] = useState('tests');
  const [expandedTests, setExpandedTests] = useState({});
  const selectedRunRef = useRef(null);
  const [sseKey, setSseKey] = useState(0); // bump to force SSE reconnect on same run ID
  const [detailRun, setDetailRun] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState(new Set());
  const [showHopTests, setShowHopTests] = useState(true);
  const [showAllToCapTests, setShowAllToCapTests] = useState(true);
  const [showPathAnalysis, setShowPathAnalysis] = useState(true);
  // Compare mode: up to two picks, each a whole test (all its trees) or one
  // run (tree). compareResult holds the last comparison, shown in its tab.
  const [compareSelection, setCompareSelection] = useState([]);
  const compareSelectionRef = useRef([]);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareResult, setCompareResult] = useState(null);
  const [compareFilter, setCompareFilter] = useState('changed');
  const [bulkSelection, setBulkSelection] = useState([]);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [compareMode, setCompareMode] = useState(false);

  const deviceLookup = useMemo(() => buildDeviceLookup(safeDevices), [safeDevices]);
  const caps = useMemo(() =>
    safeDevices.filter(d => {
      const model = d.capabilities?.model || '';
      return model.toLowerCase().includes('cap');
    }),
    [safeDevices]
  );

  const capDefaultAppliedRef = useRef(false);
  useEffect(() => {
    const fromMap = (initialCapSerials || []).filter(Boolean);
    const focusFromMap = (initialFocusSerials || []).filter(Boolean);
    if (fromMap.length === 0 && focusFromMap.length === 0) return;
    if (fromMap.length > 0 && caps.length > 0) {
      const capSet = new Set(caps.map((d) => d.serial_number));
      const matched = fromMap.filter((serial) => capSet.has(serial));
      setSelectedCapSerials(matched.length > 0 ? matched : fromMap);
      capDefaultAppliedRef.current = true;
    }
    if (focusFromMap.length > 0) {
      setMapFocusSerials(focusFromMap);
    }
    if (fromMap.length > 0 || focusFromMap.length > 0) {
      onCapScopeApplied?.();
    }
  }, [initialCapSerials, initialFocusSerials, caps, onCapScopeApplied]);

  useEffect(() => {
    if (capDefaultAppliedRef.current || caps.length === 0) return;
    if ((initialCapSerials || []).length > 0) return;
    setSelectedCapSerials([caps[0].serial_number]);
    capDefaultAppliedRef.current = true;
  }, [caps, initialCapSerials]);

  const handleCapSelectionChange = (event) => {
    const value = event.target.value;
    const next = typeof value === 'string' ? value.split(',') : value;
    if (next[next.length - 1] === '__all__') {
      setSelectedCapSerials(
        selectedCapSerials.length === caps.length
          ? []
          : caps.map((d) => d.serial_number),
      );
      return;
    }
    setSelectedCapSerials(next.filter((v) => v !== '__all__'));
  };

  const capSelectionLabel = useMemo(() => {
    if (selectedCapSerials.length === 0) return 'Select CAPs';
    if (caps.length > 1 && selectedCapSerials.length === caps.length) {
      return `All CAPs (${caps.length})`;
    }
    if (selectedCapSerials.length === 1) {
      return selectedCapSerials[0];
    }
    return `${selectedCapSerials.length} CAPs selected`;
  }, [selectedCapSerials, caps.length]);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listIperfTestRuns();
      setRuns(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Keep the ref in sync so SSE callbacks can always read the current selection.
  useEffect(() => { selectedRunRef.current = selectedRun; }, [selectedRun]);
  useEffect(() => { compareSelectionRef.current = compareSelection; }, [compareSelection]);

  // Runs can change status after retry/refresh. Drop non-completed selections so
  // the compare panel never compares stale or in-flight results.
  useEffect(() => {
    if (compareSelection.length === 0 || runs.length === 0) return;
    const known = new Set(runs.map((r) => r.id));
    const next = compareSelection.filter((item) => item.runIds.some((id) => known.has(id)));
    if (next.length !== compareSelection.length) setCompareSelection(next);
  }, [runs, compareSelection]);

  // Re-fetch runs (and refresh the selected run detail) whenever the tab becomes active.
  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    fetchRuns();
    if (selectedRunRef.current) {
      api.getIperfTestRun(selectedRunRef.current).then(data => {
        if (!cancelled && data?.id === selectedRunRef.current) setDetailRun(data);
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [isActive, fetchRuns]);

  // SSE subscription — only while the tab is visible.
  // `active` is set to false in cleanup so any callbacks that fire
  // asynchronously after abort (race) are silently ignored.
  useEffect(() => {
    if (!isActive || !selectedRun) return;

    let active = true;
    let pollHandle = null;

    const close = api.subscribeIperfTestRun(selectedRun, {
      onSnapshot: (data) => {
        if (!active || data?.id !== selectedRunRef.current) return;
        setDetailRun(data);
      },
      onTerminal: (data) => {
        if (!active || data?.id !== selectedRunRef.current) return;
        setDetailRun(data);
        fetchRuns();
      },
      onError: () => {
        if (!active) return;
        pollHandle = setInterval(async () => {
          if (!active) { clearInterval(pollHandle); return; }
          try {
            const data = await api.getIperfTestRun(selectedRunRef.current);
            if (!active || data?.id !== selectedRunRef.current) return;
            setDetailRun(data);
            if (isTerminal(data?.status)) {
              clearInterval(pollHandle);
              fetchRuns();
            }
          } catch (_) { /* ignore */ }
        }, 30000);
      },
    });

    return () => {
      active = false;
      close();
      if (pollHandle) clearInterval(pollHandle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, selectedRun, sseKey, fetchRuns]);

  const handleStart = async () => {
    const capPayload = buildIperfCapPayload(selectedCapSerials, caps);
    if (!capPayload) return;
    if (!includeHopTests && !includeE2ETests && !includeAllToCapTests) {
      setError('Select at least one test type before starting.');
      return;
    }
    setStartLoading(true);
    setError(null);
    setQueuedInfo(null);
    try {
      const payload = {
        ...capPayload,
        duration,
        protocol,
        udp_bandwidth: udpBandwidth,
        udp_bandwidth_auto: protocol === 'UDP' && udpBandwidthAuto,
        parallel_trees: selectedCapSerials.length > 1 ? parallelTrees : 1,
        include_hop: includeHopTests,
        include_e2e: includeE2ETests,
        include_all_to_cap: includeAllToCapTests,
        reverse_direction: reverseDirection,
      };
      if (mapFocusSerials.length > 0) {
        payload.focus_serials = mapFocusSerials;
      }
      const trimmedName = testName.trim();
      if (trimmedName) {
        payload.name = trimmedName;
      } else {
        const autoName = defaultIperfRunName(selectedCapSerials, caps);
        if (autoName) payload.name = autoName;
      }
      const response = await api.createIperfTestRun(payload);
      const isQueued = response?.queued === true;
      await fetchRuns();
      // The new test is the newest, so it shows expanded on the list.
      showTestsTab();
      if (isQueued) {
        setQueuedInfo('Test queued — will start automatically when the current run finishes.');
      }
    } catch (e) {
      setError(e.message || 'Failed to start test');
    } finally {
      setStartLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await api.cancelIperfTestRun(id);
      await fetchRuns();
      if (selectedRun === id) {
        const data = await api.getIperfTestRun(id);
        if (data?.id === id) setDetailRun(data);
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const clearRunFromLocalState = useCallback((id) => {
    if (selectedRun === id) {
      setSelectedRun(null);
      setDetailRun(null);
      setActiveTab('tests');
    }
    setRunTabs((prev) => prev.filter((runId) => runId !== id));
    setCompareSelection((prev) => prev.filter((item) => !item.runIds.includes(id)));
    setBulkSelection((prev) => prev.filter((runId) => runId !== id));
    setRuns((prev) => prev.filter((run) => run.id !== id));
  }, [selectedRun]);

  const handleDelete = async (id) => {
    const tryDelete = () => api.deleteIperfTestRun(id);
    try {
      await tryDelete();
      clearRunFromLocalState(id);
      await fetchRuns();
    } catch (e) {
      const msg = e?.message || '';
      if (msg.toLowerCase().includes('cancel it first')) {
        try {
          await api.cancelIperfTestRun(id);
          await tryDelete();
          clearRunFromLocalState(id);
          await fetchRuns();
          return;
        } catch (retryErr) {
          setError(retryErr?.message || msg);
          return;
        }
      }
      setError(msg);
    }
  };

  // Compare picks: a whole test (every tree) or a single run.
  const toggleComparePick = useCallback((item) => {
    if (compareLoading) return;
    setCompareSelection((prev) => {
      if (prev.some((x) => x.id === item.id)) return prev.filter((x) => x.id !== item.id);
      if (prev.length >= 2) return prev;
      return [...prev, item];
    });
  }, [compareLoading]);
  const comparePicked = (id) => compareSelection.some((x) => x.id === id);
  const compareFull = compareSelection.length >= 2;

  const handleToggleRowSelect = useCallback((run) => {
    if (compareMode) {
      if (run.status !== 'completed') return;
      toggleComparePick({ kind: 'run', id: `run:${run.id}`, runIds: [run.id], label: runDisplayName(run, deviceLookup), createdAt: run.created_at });
      return;
    }
    setBulkSelection((prev) => (
      prev.includes(run.id) ? prev.filter((runId) => runId !== run.id) : [...prev, run.id]
    ));
  }, [compareMode, toggleComparePick, deviceLookup]);

  const handleToggleAllRows = useCallback(() => {
    if (compareMode) return;
    setBulkSelection((prev) => (
      prev.length === runs.length ? [] : runs.map((run) => run.id)
    ));
  }, [compareMode, runs]);

  const handleCompareModeToggle = useCallback(() => {
    setCompareMode((on) => {
      if (on) {
        setCompareSelection([]);
      } else {
        setBulkSelection([]);
      }
      return !on;
    });
  }, []);

  const handleDeleteBulk = useCallback(async (ids) => {
    const targets = (ids || []).filter(Boolean);
    if (targets.length === 0) return;
    if (!window.confirm(
      `Delete ${targets.length} test run${targets.length === 1 ? '' : 's'} from history?`,
    )) {
      return;
    }
    setDeleteBusy(true);
    setError(null);
    try {
      const deleteOne = async (id) => {
        try {
          await api.deleteIperfTestRun(id);
          return { id, ok: true };
        } catch (err) {
          const msg = err?.message || '';
          if (!msg.toLowerCase().includes('cancel it first')) {
            throw err;
          }
          await api.cancelIperfTestRun(id);
          await api.deleteIperfTestRun(id);
          return { id, ok: true };
        }
      };
      const results = await Promise.allSettled(targets.map((id) => deleteOne(id)));
      const failed = results.filter((r) => r.status === 'rejected');
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') clearRunFromLocalState(targets[idx]);
      });
      await fetchRuns();
      if (failed.length > 0) {
        const msg = failed[0].reason?.message || 'Some test runs could not be deleted';
        setError(failed.length === targets.length ? msg : `${failed.length} of ${targets.length} deletes failed`);
      }
    } finally {
      setDeleteBusy(false);
    }
  }, [clearRunFromLocalState, fetchRuns]);

  const handleDeleteSelected = () => handleDeleteBulk(bulkSelection);

  const handleDeleteAllRuns = () => handleDeleteBulk(runs.map((run) => run.id));

  const handleRetry = async (id) => {
    if (singleRerunTestId) return;
    setRetryLoading(true);
    setError(null);
    try {
      // Queued when another iperf run is active: the response wraps the run.
      const { run, queued } = unwrapIperfRetryResponse(await api.retryIperfTestRun(id));
      if (run?.id === id) setDetailRun(run);
      if (queued) setQueuedInfo('Retry queued — it starts when the iperf test running now finishes.');
      setSseKey(k => k + 1);
      await fetchRuns();
    } catch (e) {
      setError(e.message || 'Failed to retry');
    } finally {
      setRetryLoading(false);
    }
  };

  const handleRerunSingleTest = async (testId) => {
    if (!detailRun?.id || !testId || singleRerunTestId || retryLoading || !isTerminal(detailRun.status)) return;
    setSingleRerunTestId(testId);
    setError(null);
    const runId = detailRun.id;
    try {
      // Queued when another iperf run is active: the response wraps the run.
      const { run, queued } = unwrapIperfRetryResponse(await api.retryIperfSingleTest(runId, testId));
      if (run?.id === runId) setDetailRun(run);
      if (queued) setQueuedInfo('Re-run queued — it starts when the iperf test running now finishes.');
      setSseKey(k => k + 1); // force SSE reconnect — run ID unchanged but status is now "running" or "queued"
      await fetchRuns();
    } catch (e) {
      setError(e.message || 'Failed to re-run test');
    } finally {
      setSingleRerunTestId(null);
    }
  };

  // Opened from elsewhere (e.g. Iperf Schedule's "Open in Iperf tab").
  useEffect(() => {
    if (!initialRunId) return;
    handleSelectRun(initialRunId);
    onInitialRunApplied?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRunId]);

  const handleSelectRun = async (id) => {
    setRunTabs((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setActiveTab(id);
    setSelectedRun(id);
    selectedRunRef.current = id;
    setShowDetail(true);
    setExpandedPaths(new Set());
    try {
      const data = await api.getIperfTestRun(id);
      // Discard if the user switched to a different run while this fetch was in flight
      if (id === selectedRunRef.current) setDetailRun(data);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleCompareSelected = async () => {
    if (compareSelection.length !== 2 || compareLoading) return;
    const picks = [...compareSelection];
    setCompareLoading(true);
    setError(null);
    try {
      const sides = await Promise.all(picks.map(async (item) => {
        const details = await Promise.all(item.runIds.map((id) => api.getIperfTestRun(id)));
        const completed = details.filter((r) => r?.status === 'completed');
        return { ...item, runs: completed, skipped: details.length - completed.length };
      }));
      const stillCurrent = compareSelectionRef.current.length === 2
        && picks.every((p, i) => p.id === compareSelectionRef.current[i]?.id);
      if (!stillCurrent) return;
      if (sides.some((side) => side.runs.length === 0)) {
        throw new Error('Only completed runs can be compared; one of the picks has no completed run yet.');
      }
      // The older pick is the baseline, whatever the order they were picked in.
      sides.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      const [older, newer] = sides;
      setCompareResult({ older, newer, result: compareIperfSuites(older.runs, newer.runs) });
      setCompareFilter('changed');
      setActiveTab('compare');
      setSelectedRun(null);
      selectedRunRef.current = null;
      setDetailRun(null);
    } catch (e) {
      setError(e.message || 'Failed to compare iperf test runs');
    } finally {
      setCompareLoading(false);
    }
  };

  const handleClearCompare = () => {
    setCompareSelection([]);
  };
  const closeCompareTab = () => {
    setCompareResult(null);
    if (activeTab === 'compare') showTestsTab();
  };

  const togglePath = (key) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const progress = detailRun
    ? ((detailRun.summary?.completed || 0) + (detailRun.summary?.failed || 0) +
       (detailRun.summary?.timed_out || 0) + (detailRun.summary?.cancelled || 0)) /
      Math.max(detailRun.summary?.total_tests || 1, 1) * 100
    : 0;

  const capSections = useMemo(
    () => (detailRun ? resolveCapSections(detailRun, deviceLookup) : []),
    [detailRun, deviceLookup],
  );

  const hasFailedTests = detailRun?.tests?.some(t => t.status === 'failed' || t.status === 'timed_out');
  const canRerunIndividualTests = detailRun && isTerminal(detailRun.status);
  /** While a POST is in-flight, disable all per-test buttons */
  const singleRerunGloballyBusy = singleRerunTestId != null || retryLoading;

  const tests = useMemo(() => groupIperfTests(runs), [runs]);

  const showTestsTab = () => {
    setActiveTab('tests');
    setSelectedRun(null);
    selectedRunRef.current = null;
    setDetailRun(null);
  };
  const closeRunTab = (id) => {
    const next = runTabs.filter((x) => x !== id);
    setRunTabs(next);
    if (activeTab !== id) return;
    if (next.length > 0) handleSelectRun(next[next.length - 1]);
    else showTestsTab();
  };
  const handleCancelTest = async (test) => {
    try {
      const active = test.runs.filter((r) => !isTerminal(r.status));
      await Promise.allSettled(active.map((r) => api.cancelIperfTestRun(r.id)));
      await fetchRuns();
    } catch (e) {
      setError(e.message);
    }
  };
  const tabLabelForRun = (id) => {
    const run = runs.find((r) => r.id === id) || (detailRun?.id === id ? detailRun : null);
    if (!run) return id.slice(0, 8);
    return run.name ? runDisplayName(run, deviceLookup) : runCapLabel(run, deviceLookup);
  };

  /** Header row of a test with several trees: combined status; click to expand. */
  const renderTestRow = (test, expanded) => {
    const meta = STATUS_META[test.status] || STATUS_META.pending;
    const ids = test.runs.map((r) => r.id);
    const picked = ids.filter((id) => bulkSelection.includes(id)).length;
    const otherStatuses = Object.entries(test.counts).filter(([st]) => st !== test.status);
    return (
      <TableRow
        key={`hdr-${test.id}`}
        hover
        onClick={() => setExpandedTests((prev) => ({ ...prev, [test.id]: !expanded }))}
        sx={{ cursor: 'pointer', '& > td': { bgcolor: 'action.hover' } }}
      >
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          <Tooltip title={compareMode
            ? (test.counts.completed ? 'Compare this whole test (every tree)' : 'No completed tree to compare yet')
            : 'Select every run of this test for deletion'}
          >
            <span>
              <Checkbox
                size="small"
                checked={compareMode ? comparePicked(`test:${test.id}`) : picked === ids.length}
                indeterminate={!compareMode && picked > 0 && picked < ids.length}
                disabled={deleteBusy || (compareMode && (!test.counts.completed || (compareFull && !comparePicked(`test:${test.id}`))))}
                onChange={() => (compareMode
                  ? toggleComparePick({ kind: 'test', id: `test:${test.id}`, runIds: ids, label: test.name || 'Iperf test', createdAt: test.createdAt })
                  : setBulkSelection((prev) => (picked === ids.length
                    ? prev.filter((id) => !ids.includes(id))
                    : Array.from(new Set([...prev, ...ids])))))}
              />
            </span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            <Box>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 240 }}>
                {test.name || 'Iperf test'}
              </Typography>
              {test.createdBy && <Typography variant="caption" color="text.secondary">{test.createdBy}</Typography>}
            </Box>
          </Stack>
        </TableCell>
        <TableCell>{test.runs.length} trees</TableCell>
        <TableCell>
          <Chip size="small" label={meta.label} color={meta.color} />
          {otherStatuses.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block">
              {otherStatuses.map(([st, n]) => `${n} ${(STATUS_META[st] || { label: st }).label.toLowerCase()}`).join(' · ')}
            </Typography>
          )}
        </TableCell>
        <TableCell>{test.totalTests > 0 ? `${test.completedTests}/${test.totalTests}` : '—'}</TableCell>
        <TableCell>
          {test.healthy > 0 && <Chip size="small" label={`${test.healthy} healthy`} color="success" variant="outlined" sx={{ mr: 0.5 }} />}
          {test.degraded > 0 && <Chip size="small" label={`${test.degraded} degraded`} color="error" variant="outlined" />}
        </TableCell>
        <TableCell>{relativeTime(test.createdAt)}</TableCell>
        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
          {test.active > 0 && (
            <Tooltip title={`Stop the test (${test.active} tree${test.active === 1 ? '' : 's'} running or waiting)`}>
              <IconButton size="small" onClick={() => handleCancelTest(test)} disabled={deleteBusy}>
                <StopIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete every run of this test">
            <span>
              <IconButton size="small" color="error" disabled={deleteBusy} onClick={() => handleDeleteBulk(ids)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  /** One run row of the test list; nested under its test header when the test has several trees. */
  const renderRunRow = (run, nested = false) => {
    const meta = STATUS_META[run.status] || STATUS_META.pending;
    const prog = run.summary?.total_tests > 0
      ? `${(run.summary?.completed || 0)}/${run.summary?.total_tests}`
      : '—';
    const selectedForCompare = comparePicked(`run:${run.id}`);
    const canCompareRun = run.status === 'completed';
    const compareDisabled = compareLoading || !canCompareRun || (!selectedForCompare && compareFull);
    return (
      <TableRow
        key={run.id}
        hover
        selected={selectedRun === run.id}
        onClick={() => handleSelectRun(run.id)}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          <Tooltip title={
            compareMode
              ? (canCompareRun
                ? (compareDisabled ? 'Clear a pick first (max 2)' : 'Compare this tree on its own')
                : 'Only completed runs can be compared')
              : 'Select for deletion'
          }
          >
            <span>
              <Checkbox
                size="small"
                checked={compareMode ? selectedForCompare : bulkSelection.includes(run.id)}
                disabled={
                  deleteBusy
                  || (compareMode && (compareDisabled || !canCompareRun))
                }
                onChange={() => handleToggleRowSelect(run)}
              />
            </span>
          </Tooltip>
        </TableCell>
        <TableCell sx={nested ? { pl: 5 } : undefined}>
          <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 240 }}>
            {nested ? runCapLabel(run, deviceLookup) : runDisplayName(run, deviceLookup)}
          </Typography>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
          {runCapLabel(run, deviceLookup)}
        </TableCell>
        <TableCell><Chip size="small" label={meta.label} color={meta.color} /></TableCell>
        <TableCell>{prog}</TableCell>
        <TableCell>
          {run.summary?.healthy_paths > 0 && (
            <Chip size="small" label={`${run.summary.healthy_paths} healthy`} color="success" variant="outlined" sx={{ mr: 0.5 }} />
          )}
          {run.summary?.degraded_paths > 0 && (
            <Chip size="small" label={`${run.summary.degraded_paths} degraded`} color="error" variant="outlined" />
          )}
        </TableCell>
        <TableCell>{relativeTime(run.created_at)}</TableCell>
        <TableCell align="right">
          {run.status === 'running' && (
            <Tooltip title="Cancel active test">
              <IconButton size="small" onClick={e => { e.stopPropagation(); handleCancel(run.id); }} disabled={deleteBusy}>
                <StopIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={run.status === 'running'
            ? 'Remove stuck run (not active on server)'
            : 'Delete from history'}
          >
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={deleteBusy}
                onClick={e => { e.stopPropagation(); handleDelete(run.id); }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Start controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <SpeedIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>Tree-Wide Iperf Test</Typography>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>CAP Devices</InputLabel>
            <Select
              multiple
              value={selectedCapSerials}
              onChange={handleCapSelectionChange}
              label="CAP Devices"
              renderValue={() => capSelectionLabel}
            >
              {caps.length > 1 && (
                <MenuItem value="__all__">
                  <Checkbox
                    checked={selectedCapSerials.length === caps.length && caps.length > 0}
                    indeterminate={
                      selectedCapSerials.length > 0 && selectedCapSerials.length < caps.length
                    }
                  />
                  <ListItemText primary={`All CAPs (${caps.length})`} />
                </MenuItem>
              )}
              {caps.map((d) => (
                <MenuItem key={d.serial_number} value={d.serial_number}>
                  <Checkbox checked={selectedCapSerials.indexOf(d.serial_number) > -1} />
                  <ListItemText
                    primary={d.serial_number}
                    secondary={d.name && d.name !== d.serial_number ? d.name : undefined}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={startLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleStart}
            disabled={selectedCapSerials.length === 0 || startLoading}
          >
            Start Test
          </Button>
          <Button size="small" onClick={() => setShowParams(!showParams)}>
            {showParams ? 'Hide' : 'Show'} Parameters
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Each selected CAP starts its own test run. Multiple CAPs run one after another, or several at a time (Trees in parallel, under Parameters).
        </Typography>
        {mapFocusSerials.length > 0 && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Map selection: iperf is limited to {mapFocusSerials.length} selected device(s) and their uplink paths under the chosen CAP(s).
          </Alert>
        )}
        <Collapse in={showParams}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              size="small"
              label="Test Name"
              value={testName}
              onChange={e => setTestName(e.target.value)}
              placeholder={defaultIperfRunName(selectedCapSerials, caps) || 'Optional run name'}
              sx={{ maxWidth: 420 }}
            />
            <IperfTestSettingsFields
              value={{
                duration,
                protocol,
                udpBandwidth,
                udpBandwidthAuto,
                parallelTrees,
                includeHop: includeHopTests,
                includeE2E: includeE2ETests,
                includeAllToCap: includeAllToCapTests,
                reverseDirection,
              }}
              treeCount={selectedCapSerials.length}
              onChange={(patch) => {
                if ('duration' in patch) setDuration(patch.duration);
                if ('protocol' in patch) setProtocol(patch.protocol);
                if ('udpBandwidth' in patch) setUdpBandwidth(patch.udpBandwidth);
                if ('udpBandwidthAuto' in patch) setUdpBandwidthAuto(patch.udpBandwidthAuto);
                if ('parallelTrees' in patch) setParallelTrees(patch.parallelTrees);
                if ('includeHop' in patch) setIncludeHopTests(patch.includeHop);
                if ('includeE2E' in patch) setIncludeE2ETests(patch.includeE2E);
                if ('includeAllToCap' in patch) setIncludeAllToCapTests(patch.includeAllToCap);
                if ('reverseDirection' in patch) setReverseDirection(patch.reverseDirection);
              }}
            />
          </Stack>
        </Collapse>
      </Paper>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {queuedInfo && <Alert severity="info" onClose={() => setQueuedInfo(null)} sx={{ mb: 2 }}>{queuedInfo}</Alert>}

      {/* Tabs: the test list, plus one closable tab per opened run */}
      {(runTabs.length > 0 || compareResult) && (
        <Tabs
          value={activeTab}
          onChange={(_e, v) => {
            if (v === 'tests') showTestsTab();
            else if (v === 'compare') { setActiveTab('compare'); setSelectedRun(null); selectedRunRef.current = null; setDetailRun(null); }
            else handleSelectRun(v);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
        >
          <Tab value="tests" label="Tests" sx={{ textTransform: 'none', minHeight: 40 }} />
          {compareResult && (
            <Tab
              value="compare"
              sx={{ textTransform: 'none', minHeight: 40, pr: 0.5 }}
              label={(
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>Compare: {compareResult.older.label} → {compareResult.newer.label}</Typography>
                  <IconButton size="small" component="span" aria-label="Close compare" onClick={(e) => { e.stopPropagation(); closeCompareTab(); }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              )}
            />
          )}
          {runTabs.map((id) => (
            <Tab
              key={id}
              value={id}
              sx={{ textTransform: 'none', minHeight: 40, pr: 0.5 }}
              label={(
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{tabLabelForRun(id)}</Typography>
                  <IconButton
                    size="small"
                    component="span"
                    aria-label="Close tab"
                    onClick={(e) => { e.stopPropagation(); closeRunTab(id); }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              )}
            />
          ))}
        </Tabs>
      )}

      {activeTab === 'tests' && (
      <>
      {/* Test runs list */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
          sx={{ p: 1.5 }}
        >
          <Typography variant="subtitle2">Test Runs</Typography>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant={compareMode ? 'contained' : 'outlined'}
              onClick={handleCompareModeToggle}
              disabled={deleteBusy || loading}
            >
              {compareMode ? 'Exit compare' : 'Compare mode'}
            </Button>
            {!compareMode && (
              <>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={bulkSelection.length === 0 || deleteBusy || loading}
                  onClick={handleDeleteSelected}
                >
                  Delete selected ({bulkSelection.length})
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={runs.length === 0 || deleteBusy || loading}
                  onClick={handleDeleteAllRuns}
                >
                  Delete all
                </Button>
              </>
            )}
            <IconButton size="small" onClick={fetchRuns} disabled={deleteBusy}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Tooltip title={
                    compareMode
                      ? 'Select up to two completed runs to compare'
                      : 'Select runs to delete'
                  }
                  >
                    <span>
                      <Checkbox
                        size="small"
                        checked={
                          compareMode
                            ? compareSelection.length === runs.filter((r) => r.status === 'completed').length
                              && runs.filter((r) => r.status === 'completed').length > 0
                            : runs.length > 0 && bulkSelection.length === runs.length
                        }
                        indeterminate={
                          compareMode
                            ? compareSelection.length > 0 && compareSelection.length < 2
                            : bulkSelection.length > 0 && bulkSelection.length < runs.length
                        }
                        disabled={runs.length === 0 || deleteBusy || compareMode}
                        onChange={handleToggleAllRows}
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>CAP</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Analysis</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">No test runs yet</Typography>
                  </TableCell>
                </TableRow>
              )}
              {tests.map((test) => {
                if (test.runs.length === 1) return renderRunRow(test.runs[0]);
                const expanded = Boolean(expandedTests[test.id]);
                return (
                  <React.Fragment key={test.id}>
                    {renderTestRow(test, expanded)}
                    {expanded && test.runs.map((run) => renderRunRow(run, true))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}
        >
          <Typography variant="caption" color="text.secondary">
            {compareMode
              ? `Pick two: whole tests (header checkbox, every tree) or single trees, even from different tests. Matching tests are judged better or worse; a change counts from ${IPERF_COMPARE_DIFF_PCT}% throughput or ${IPERF_COMPARE_LOSS_PP} pp loss.`
              : 'Turn on Compare mode to judge one test against another: newer vs older, test by test.'}
          </Typography>
          {compareMode && (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {compareSelection.length > 0 && (
                <Button size="small" onClick={handleClearCompare}>
                  Clear
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                disabled={compareSelection.length !== 2 || compareLoading}
                onClick={handleCompareSelected}
                startIcon={compareLoading ? <CircularProgress size={14} /> : null}
              >
                Compare ({compareSelection.length}/2)
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      </>
      )}

      {activeTab === 'compare' && compareResult && (() => {
        const { older, newer, result } = compareResult;
        const { summary } = result;
        const rows = filterCompareRows(result.rows, compareFilter);
        const verdictMeta = {
          better: { color: 'success', label: 'Better' }, worse: { color: 'error', label: 'Worse' },
          mixed: { color: 'warning', label: 'Mixed' }, same: { color: 'default', label: 'Same' },
        };
        const pickLabel = (side) => `${side.label} (${runLabel(side.runs[0], deviceLookup).split(' · ').pop()}${side.kind === 'test' ? `, ${side.runs.length} tree${side.runs.length === 1 ? '' : 's'}` : ''}${side.skipped ? `, ${side.skipped} not completed` : ''})`;
        return (
          <Paper variant="outlined">
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>Compare iperf tests</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Older (baseline): {pickLabel(older)} · Newer: {pickLabel(newer)}
              </Typography>
              <Alert
                severity={result.overall === 'better' ? 'success' : result.overall === 'worse' ? 'error' : result.overall === 'mixed' ? 'warning' : 'info'}
                sx={{ mt: 1.5 }}
              >
                {describeCompareSummary(result)}
                {(summary.unmatchedOlder > 0 || summary.unmatchedNewer > 0) && (
                  ` ${summary.unmatchedOlder} test${summary.unmatchedOlder === 1 ? '' : 's'} only in the older, ${summary.unmatchedNewer} only in the newer (different links or trees).`
                )}
              </Alert>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Chip size="small" label={`${summary.matched} matched`} />
                <Chip size="small" color="success" variant="outlined" label={`${summary.throughputImproved} faster`} />
                <Chip size="small" color="error" variant="outlined" label={`${summary.throughputDegraded} slower`} />
                <Chip size="small" color="success" variant="outlined" label={`${summary.lossImproved} less loss`} />
                <Chip size="small" color="error" variant="outlined" label={`${summary.lossDegraded} more loss`} />
              </Stack>
              {result.trees.length > 1 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {result.trees.map((t) => (
                    <Tooltip key={t.cap} title={`${t.matched} matched: ${t.better} better, ${t.worse} worse, ${t.mixed} mixed, ${t.same} unchanged`}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={t.worse > t.better ? 'error' : t.better > t.worse ? 'success' : 'default'}
                        label={`${runCapLabel({ cap_serial: t.cap }, deviceLookup)}: ${t.better}↑ ${t.worse}↓`}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              )}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
                {[['changed', `Changed (${summary.matched - summary.same})`], ['worse', `Worse (${summary.worse + summary.mixed})`], ['better', `Better (${summary.better})`], ['all', `All (${summary.matched})`]].map(([v, l]) => (
                  <Chip key={v} size="small" label={l} color={compareFilter === v ? 'primary' : 'default'} variant={compareFilter === v ? 'filled' : 'outlined'} onClick={() => setCompareFilter(v)} />
                ))}
              </Stack>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Link</TableCell>
                    <TableCell align="right">Throughput (older → newer)</TableCell>
                    <TableCell align="right">Loss (older → newer)</TableCell>
                    <TableCell>Verdict</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No tests in this filter.</Typography></TableCell></TableRow>
                  )}
                  {rows.map((row) => (
                    <TableRow key={row.key} hover>
                      <TableCell><Chip size="small" variant="outlined" label={iperfCompareTypeLabel(row.type)} /></TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{String(row.from).slice(-6)} → {String(row.to).slice(-6)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={row.throughputSignificant ? (row.throughputDelta >= 0 ? 'success.main' : 'error.main') : 'text.secondary'}>
                          {fmt(row.beforeMbps)} → {fmt(row.afterMbps)} Mbps
                        </Typography>
                        {row.throughputDelta != null && (
                          <Typography variant="caption" color="text.secondary">{fmtSigned(row.throughputDelta)} Mbps ({fmtSigned(row.throughputPct)}%)</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={row.lossSignificant ? (row.lossDeltaPP <= 0 ? 'success.main' : 'error.main') : 'text.secondary'}>
                          {row.beforeLoss == null || row.afterLoss == null ? '—' : `${fmt(row.beforeLoss)}% → ${fmt(row.afterLoss)}%`}
                        </Typography>
                        {row.lossDeltaPP != null && <Typography variant="caption" color="text.secondary">{fmtSigned(row.lossDeltaPP)} pp</Typography>}
                      </TableCell>
                      <TableCell><Chip size="small" color={verdictMeta[row.verdict].color} variant={row.verdict === 'same' ? 'outlined' : 'filled'} label={verdictMeta[row.verdict].label} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })()}

      {/* Detail view: the active run tab */}
      {activeTab !== 'tests' && activeTab !== 'compare' && !detailRun && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      )}
      {activeTab !== 'tests' && activeTab !== 'compare' && selectedRun && detailRun && (
        <Paper variant="outlined">
          {/* Clickable header — always visible, toggles content */}
          <Stack
            direction="row" justifyContent="space-between" alignItems="center"
            onClick={() => setShowDetail(v => !v)}
            sx={{ p: 2, cursor: 'pointer', userSelect: 'none' }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton size="small" tabIndex={-1}>
                {showDetail ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
              </IconButton>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Test Run — {runDisplayName(detailRun, deviceLookup)}
                </Typography>
                {detailRun.name && (
                  <Typography variant="caption" color="text.secondary">
                    CAP: {runCapLabel(detailRun, deviceLookup)}
                  </Typography>
                )}
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                variant="outlined"
                label={detailRun.reverse_direction ? 'From CAP' : 'Toward CAP'}
                color={detailRun.reverse_direction ? 'secondary' : 'default'}
              />
              {isTerminal(detailRun.status) && hasFailedTests && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={retryLoading ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                  onClick={e => { e.stopPropagation(); handleRetry(detailRun.id); }}
                  disabled={retryLoading || !!singleRerunTestId}
                >
                  Retry Failed
                </Button>
              )}
              <Chip
                size="small"
                label={(STATUS_META[detailRun.status] || STATUS_META.pending).label}
                color={(STATUS_META[detailRun.status] || STATUS_META.pending).color}
              />
            </Stack>
          </Stack>

          <Collapse in={showDetail} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, pb: 2 }}>
          {!isTerminal(detailRun.status) && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {detailRun.summary?.completed || 0} / {detailRun.summary?.total_tests || 0} tests completed
              </Typography>
            </Box>
          )}

          {capSections.map((section) => {
            const sectionLabels = getIperfCoverageLabels(section.reverse);
            return (
            <Box key={section.capSerial} sx={{ mb: capSections.length > 1 ? 3 : 0 }}>
              {capSections.length > 1 && (
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 1.5, fontFamily: 'monospace' }}
                >
                  CAP {section.capSerial}
                </Typography>
              )}

          {/* Hop tests */}
          {section.hopTests.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stack
                direction="row" alignItems="center" spacing={0.5}
                onClick={() => setShowHopTests(v => !v)}
                sx={{ cursor: 'pointer', mb: showHopTests ? 1 : 0, userSelect: 'none' }}
              >
                <IconButton size="small" tabIndex={-1}>
                  {showHopTests ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
                <Typography variant="subtitle2">
                  Link tests — {sectionLabels.hop.title}
                </Typography>
              </Stack>
              <Collapse in={showHopTests} timeout="auto" unmountOnExit>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>From → To</TableCell>
                      <TableCell>Ch / Band</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Throughput</TableCell>
                      <TableCell align="right">Loss</TableCell>
                      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{RF_STATS_TOOLTIP}</span>} placement="top">
                        <TableCell align="right" sx={{ cursor: 'help' }}>RF Stats</TableCell>
                      </Tooltip>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap', width: 72 }}>
                        Again
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {section.hopTests.map(t => {
                      const meta = STATUS_META[t.status] || STATUS_META.pending;
                      const chBand = resolveChannelBand(t, deviceLookup, section.reverse);
                      const rerunBusyRow = singleRerunTestId === t.id;
                      return (
                        <TableRow key={t.id}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {t.from_serial?.slice(-6)} → {t.to_serial?.slice(-6)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                            {chBand || '—'}
                          </TableCell>
                          <TableCell>
                            {t.status === 'running'
                              ? <CircularProgress size={14} />
                              : <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
                            }
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={describeOfferedRate(t)} disableHoverListener={!describeOfferedRate(t)}>
                              <span>{t.status === 'completed' ? `${fmt(resolveThroughput(t))} Mbps` : '—'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{t.status === 'completed' ? (resolveLoss(t) != null ? `${fmt(resolveLoss(t))}%` : '—') : '—'}</TableCell>
                          {t.status === 'completed' ? <RfStatsCell test={t} /> : <TableCell align="right">—</TableCell>}
                          <TableCell align="center">
                            <Tooltip title={canRerunIndividualTests ? 'Re-run this hop test using the same run parameters' : 'Available when run is finished'}>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Re-run hop ${t.from_serial} to ${t.to_serial}`}
                                  onClick={() => handleRerunSingleTest(t.id)}
                                  disabled={
                                    !canRerunIndividualTests
                                    || singleRerunGloballyBusy
                                  }
                                >
                                  {rerunBusyRow ? <CircularProgress size={14} /> : <ReplayIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              </Collapse>
            </Box>
          )}

          {section.allToCapTests.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stack
                direction="row" alignItems="center" spacing={0.5}
                onClick={() => setShowAllToCapTests(v => !v)}
                sx={{ cursor: 'pointer', mb: showAllToCapTests ? 1 : 0, userSelect: 'none' }}
              >
                <IconButton size="small" tabIndex={-1}>
                  {showAllToCapTests ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
                <Typography variant="subtitle2">
                  Direct to CAP — {sectionLabels.allToCap.title}
                </Typography>
              </Stack>
              <Collapse in={showAllToCapTests} timeout="auto" unmountOnExit>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>From → To</TableCell>
                      <TableCell>Ch / Band</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Throughput</TableCell>
                      <TableCell align="right">Loss</TableCell>
                      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{RF_STATS_TOOLTIP}</span>} placement="top">
                        <TableCell align="right" sx={{ cursor: 'help' }}>RF Stats</TableCell>
                      </Tooltip>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap', width: 72 }}>
                        Again
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {section.allToCapTests.map(t => {
                      const meta = STATUS_META[t.status] || STATUS_META.pending;
                      const chBand = resolveChannelBand(t, deviceLookup, section.reverse);
                      const rerunBusyRow = singleRerunTestId === t.id;
                      return (
                        <TableRow key={t.id}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {t.from_serial?.slice(-6)} → {t.to_serial?.slice(-6)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                            {chBand || '—'}
                          </TableCell>
                          <TableCell>
                            {t.status === 'running'
                              ? <CircularProgress size={14} />
                              : <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
                            }
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={describeOfferedRate(t)} disableHoverListener={!describeOfferedRate(t)}>
                              <span>{t.status === 'completed' ? `${fmt(resolveThroughput(t))} Mbps` : '—'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{t.status === 'completed' ? (resolveLoss(t) != null ? `${fmt(resolveLoss(t))}%` : '—') : '—'}</TableCell>
                          {t.status === 'completed' ? <RfStatsCell test={t} /> : <TableCell align="right">—</TableCell>}
                          <TableCell align="center">
                            <Tooltip title={canRerunIndividualTests ? 'Re-run this test using the same run parameters' : 'Available when run is finished'}>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Re-run ${t.from_serial} to ${t.to_serial}`}
                                  onClick={() => handleRerunSingleTest(t.id)}
                                  disabled={!canRerunIndividualTests || singleRerunGloballyBusy}
                                >
                                  {rerunBusyRow ? <CircularProgress size={14} /> : <ReplayIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              </Collapse>
            </Box>
          )}

          {/* Path Analysis for End to End — merged E2E + analysis, expandable per-hop */}
          {section.e2eTests.length > 0 && (
            <Box>
              <Stack
                direction="row" alignItems="center" spacing={0.5}
                onClick={() => setShowPathAnalysis(v => !v)}
                sx={{ cursor: 'pointer', mb: showPathAnalysis ? 1 : 0, userSelect: 'none' }}
              >
                <IconButton size="small" tabIndex={-1}>
                  {showPathAnalysis ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
                <Typography variant="subtitle2">
                  End-to-end — {sectionLabels.e2e.title}
                </Typography>
              </Stack>
              <Collapse in={showPathAnalysis} timeout="auto" unmountOnExit>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Path</TableCell>
                      <TableCell>Hops</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">E2E</TableCell>
                      <TableCell align="right">Loss</TableCell>
                      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{RF_STATS_TOOLTIP}</span>} placement="top">
                        <TableCell align="right" sx={{ cursor: 'help' }}>RF Stats</TableCell>
                      </Tooltip>
                      <TableCell align="right">Bottleneck</TableCell>
                      <TableCell align="right">Expected</TableCell>
                      <TableCell align="right">Degradation</TableCell>
                      <TableCell>Health</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap', width: 72 }}>
                        Again
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {section.e2eTests.map((t, i) => {
                      const dp = buildPathForE2ETest(t, section.analysisPaths, detailRun.tests || []);
                      const expandKey = pathExpandKey(section.capSerial, i);
                      const expanded = expandedPaths.has(expandKey);
                      const eMeta = STATUS_META[t.status] || STATUS_META.pending;
                      const done = t.status === 'completed';
                      const hasAnalysis = typeof dp.bottleneck_hop_mbps === 'number' && dp.bottleneck_hop_mbps > 0;
                      const rerunBusyRow = singleRerunTestId === t.id;
                      return (
                        <React.Fragment key={t.id}>
                          <TableRow
                            hover
                            onClick={() => togglePath(expandKey)}
                            sx={{ cursor: 'pointer', '& > *': { borderBottom: expanded ? 'unset' : undefined } }}
                          >
                            <TableCell padding="checkbox">
                              <IconButton size="small">
                                {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {t.from_serial?.slice(-6)} → {t.to_serial?.slice(-6)}
                            </TableCell>
                            <TableCell>{t.path_hops || '—'}</TableCell>
                            <TableCell>
                              {t.status === 'running'
                                ? <CircularProgress size={14} />
                                : <Chip size="small" label={eMeta.label} color={eMeta.color} variant="outlined" />
                              }
                            </TableCell>
                            <TableCell align="right">
                            <Tooltip title={describeOfferedRate(t)} disableHoverListener={!describeOfferedRate(t)}>
                              <span>{done ? `${fmt(resolveThroughput(t))} Mbps` : '—'}</span>
                            </Tooltip>
                          </TableCell>
                            <TableCell align="right">{done ? (resolveLoss(t) != null ? `${fmt(resolveLoss(t))}%` : '—') : '—'}</TableCell>
                            {done ? <RfStatsCell test={t} /> : <TableCell align="right">—</TableCell>}
                            <TableCell align="right">{hasAnalysis ? `${fmt(dp.bottleneck_hop_mbps)} Mbps` : '—'}</TableCell>
                            <TableCell align="right">{hasAnalysis ? `≥${fmt(dp.expected_e2e_mbps)} Mbps` : '—'}</TableCell>
                            <TableCell align="right">
                              {hasAnalysis && typeof dp.degradation_pct === 'number'
                                ? `${fmt(dp.degradation_pct)}%`
                                : 'None'}
                            </TableCell>
                            <TableCell>
                              {dp.healthy === true && (
                                <Chip size="small" icon={<CheckCircleIcon />} label="Healthy" color="success" variant="outlined" />
                              )}
                              {dp.healthy === false && hasAnalysis && (
                                <Chip size="small" icon={<WarningIcon />} label="Degraded" color="warning" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="center" onClick={e => e.stopPropagation()}>
                              <Tooltip title={canRerunIndividualTests ? 'Re-run this end-to-end test using the same run parameters' : 'Available when run is finished'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label={`Re-run E2E ${t.from_serial} to ${t.to_serial}`}
                                    onClick={() => handleRerunSingleTest(t.id)}
                                    disabled={!canRerunIndividualTests || singleRerunGloballyBusy}
                                  >
                                    {rerunBusyRow ? <CircularProgress size={14} /> : <ReplayIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={12} sx={{ py: 0, px: 0 }}>
                              <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Box sx={{ mx: 3, my: 1 }}>
                                  {dp.healthy === false && dp.flagged_hop && (
                                    <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                                      Likely cause: {dp.flagged_hop}
                                    </Alert>
                                  )}
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Hop</TableCell>
                                        <TableCell>Ch / Band</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Throughput</TableCell>
                                        <TableCell align="right">Loss</TableCell>
                                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{RF_STATS_TOOLTIP}</span>} placement="top">
                                          <TableCell align="right" sx={{ cursor: 'help' }}>RF Stats</TableCell>
                                        </Tooltip>
                                        <TableCell align="center" sx={{ width: 72 }}>
                                          Again
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {dp.hopChain.map((hop, hi) => {
                                        const meta = STATUS_META[hop.status] || STATUS_META.pending;
                                        const hopChBand = resolveChannelBand(hop, deviceLookup, section.reverse);
                                        const rerunHopBusy = hop.id && singleRerunTestId === hop.id;
                                        return (
                                          <TableRow key={hop.id || hi}>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                              {hop.from_serial?.slice(-6)} → {hop.to_serial?.slice(-6)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                              {hopChBand || '—'}
                                            </TableCell>
                                            <TableCell>
                                              <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
                                            </TableCell>
                                            <TableCell align="right">
                                              {hop.status === 'completed' ? `${fmt(resolveThroughput(hop))} Mbps` : '—'}
                                            </TableCell>
                                            <TableCell align="right">
                                              {hop.status === 'completed' ? (resolveLoss(hop) != null ? `${fmt(resolveLoss(hop))}%` : '—') : '—'}
                                            </TableCell>
                                            {hop.status === 'completed' ? <RfStatsCell test={hop} /> : <TableCell align="right">—</TableCell>}
                                            <TableCell align="center">
                                              {hop.id ? (
                                                <Tooltip title={canRerunIndividualTests ? 'Re-run this hop using the same run parameters' : 'Available when run is finished'}>
                                                  <span>
                                                    <IconButton
                                                      size="small"
                                                      aria-label="Re-run hop"
                                                      onClick={() => handleRerunSingleTest(hop.id)}
                                                      disabled={!canRerunIndividualTests || singleRerunGloballyBusy}
                                                    >
                                                      {rerunHopBusy ? <CircularProgress size={14} /> : <ReplayIcon sx={{ fontSize: 16 }} />}
                                                    </IconButton>
                                                  </span>
                                                </Tooltip>
                                              ) : '—'}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              </Collapse>
            </Box>
          )}
            </Box>
          );
          })}
          </Box>
          </Collapse>
        </Paper>
      )}
    </Box>
  );
};

export default IperfTestPanel;
